import { createContext, useContext, useCallback, useEffect, type ReactNode } from 'react';
import {
  useAuth,
  useLogout,
  useRefreshToken,
  getAccessToken,
  clearTokens,
  type User,
} from '@theraply/api-client';

interface AuthContextType {
  user: User | null | undefined;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  error: { message: string } | null;
  login: () => void;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { user, isAuthenticated, isLoading, error, refetch } = useAuth();
  const { mutateAsync: logoutMutation } = useLogout();
  const { mutateAsync: refreshMutation } = useRefreshToken();

  // Admin tokens have shorter expiry - refresh more frequently
  useEffect(() => {
    const token = getAccessToken();
    if (!token) return;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiresAt = payload.exp * 1000;
      const now = Date.now();
      const timeUntilExpiry = expiresAt - now;

      // Refresh 2 minutes before expiry for admin sessions
      const refreshTime = timeUntilExpiry - 2 * 60 * 1000;

      if (refreshTime > 0) {
        const timeoutId = setTimeout(() => {
          refreshMutation().catch(() => {
            clearTokens();
            window.location.href = '/login';
          });
        }, refreshTime);

        return () => clearTimeout(timeoutId);
      } else if (timeUntilExpiry < 0) {
        clearTokens();
        window.location.href = '/login';
      }
    } catch {
      clearTokens();
    }
  }, [refreshMutation, user]);

  // Auto-logout on inactivity (30 minutes for admin)
  useEffect(() => {
    if (!isAuthenticated) return;

    let inactivityTimer: NodeJS.Timeout;
    const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes

    const resetTimer = () => {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => {
        logoutMutation();
        window.location.href = '/login?reason=inactivity';
      }, INACTIVITY_TIMEOUT);
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => document.addEventListener(event, resetTimer));
    resetTimer();

    return () => {
      clearTimeout(inactivityTimer);
      events.forEach(event => document.removeEventListener(event, resetTimer));
    };
  }, [isAuthenticated, logoutMutation]);

  const login = useCallback(() => {
    refetch();
  }, [refetch]);

  const logout = useCallback(async () => {
    await logoutMutation();
  }, [logoutMutation]);

  const refreshSession = useCallback(async () => {
    await refreshMutation();
    refetch();
  }, [refreshMutation, refetch]);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isAdmin: user?.role === 'admin',
    isLoading,
    error,
    login,
    logout,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
